// The control primitives the filter panel renders from filterSpec.js.
//
// Each one is dumb: it takes the spec row, the current values and a setter, and owns no state.
// The panel decides layout; these decide only what a "range" or a "chips" looks like.

import CurrencyInput from "../CurrencyInput";
import { inputStyle, selectStyle, labelStyle, hintStyle, chipStyle } from "./styles";

function Field({ label, hint, children }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      {children}
      {hint && <div style={hintStyle}>{hint}</div>}
    </div>
  );
}

/** Two numeric inputs, or two dropdowns when the spec supplies `choices`. */
export function RangeField({ f, values, set }) {
  const minKey = `${f.key}_min`;
  const maxKey = `${f.key}_max`;
  if (f.choices) {
    return (
      <Field label={f.label} hint={f.hint}>
        <div style={{ display: "flex", gap: "8px" }}>
          {[[minKey, "Min"], [maxKey, "Max"]].map(([key, placeholder]) => (
            <select key={key} value={values[key]} onChange={(e) => set(key, e.target.value)} style={selectStyle}>
              <option value="">{placeholder}</option>
              {f.choices.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          ))}
        </div>
      </Field>
    );
  }
  return (
    <Field label={f.label} hint={f.hint}>
      <div style={{ display: "flex", gap: "8px" }}>
        {[[minKey, "Min"], [maxKey, "Max"]].map(([key, placeholder]) => (
          <input
            key={key}
            type="number"
            step={f.step || "any"}
            placeholder={placeholder}
            value={values[key]}
            onChange={(e) => set(key, e.target.value)}
            style={inputStyle}
          />
        ))}
      </div>
    </Field>
  );
}

/** Two CurrencyInputs — thousands separators while typing, raw numbers in state. */
export function CurrencyRangeField({ f, values, set }) {
  return (
    <Field label={f.label} hint={f.hint}>
      <div style={{ display: "flex", gap: "8px" }}>
        <CurrencyInput placeholder="Min" value={values[`${f.key}_min`]}
          onChange={(v) => set(`${f.key}_min`, v)} style={inputStyle} allowDecimals />
        <CurrencyInput placeholder="Max" value={values[`${f.key}_max`]}
          onChange={(v) => set(`${f.key}_max`, v)} style={inputStyle} allowDecimals />
      </div>
    </Field>
  );
}

export function SelectField({ f, values, set }) {
  return (
    <Field label={f.label} hint={f.hint}>
      <select value={values[f.key]} onChange={(e) => set(f.key, e.target.value)} style={selectStyle}>
        <option value="">Any</option>
        {f.options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </Field>
  );
}

/** Multi-select. An empty selection means "no constraint", not "none of them". */
export function ChipsField({ f, values, set }) {
  const selected = values[f.key] || [];
  const toggle = (v) =>
    set(f.key, selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <Field label={f.label} hint={f.hint}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", paddingTop: "2px" }}>
        {f.options.map((o) => (
          <span key={o.value} style={chipStyle(selected.includes(o.value))}
            onClick={() => toggle(o.value)} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(o.value); } }}>
            {o.label}
          </span>
        ))}
      </div>
    </Field>
  );
}

export function DaysField({ f, values, set }) {
  return (
    <Field label={f.label} hint={f.hint}>
      <select value={values[f.key]} onChange={(e) => set(f.key, e.target.value)} style={selectStyle}>
        <option value="">Any time</option>
        {f.choices.map((d) => (
          <option key={d} value={d}>{`Within ${d} days`}</option>
        ))}
      </select>
    </Field>
  );
}

export function DateRangeField({ f, values, set }) {
  return (
    <Field label={f.label} hint={f.hint}>
      <div style={{ display: "flex", gap: "8px" }}>
        <input type="date" value={values.date_from} onChange={(e) => set("date_from", e.target.value)} style={inputStyle} />
        <input type="date" value={values.date_to} onChange={(e) => set("date_to", e.target.value)} style={inputStyle} />
      </div>
    </Field>
  );
}

const BY_CONTROL = {
  range: RangeField,
  currency: CurrencyRangeField,
  select: SelectField,
  chips: ChipsField,
  days: DaysField,
  daterange: DateRangeField,
};

/** Render one spec row. Returns null for a row whose reveal condition isn't met. */
export default function FilterControl({ f, values, set }) {
  if (f.revealedBy && values[f.revealedBy.key] !== f.revealedBy.equals) return null;
  const Control = BY_CONTROL[f.control];
  if (!Control) return null;
  return <Control f={f} values={values} set={set} />;
}
